/**
 * rideAnalyticsApi.js
 *
 * Frontend API calls for Ride Analytics
 * All APIs use axios.create instance (getRideOverview pattern)
 */

import axios from "axios";

// ✅ Create axios instance
const API_URL = axios.create({
  baseURL: `${process.env.REACT_APP_SOCKET_URL}/api/ride-analytics`,
});

// ✅ Get auth header
const getAuthHeader = () => {
  const token = localStorage.getItem("token");
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

/**
 * 📊 Get ride overview (Today, Week, Month, All-time)
 */
export async function getRideOverview() {
  try {
    const res = await API_URL.get("/overview", getAuthHeader());
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.error || err.message };
  }
}

/**
 * 📅 Get daily ride stats
 */
export async function getDailyRideStats(date = null) {
  try {
    const params = date ? { date } : {};
    const res = await API_URL.get("/daily", {
      ...getAuthHeader(),
      params,
    });
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.error || err.message };
  }
}

/**
 * 📆 Get monthly ride stats
 */
export async function getMonthlyRideStats(year = null, month = null) {
  try {
    const params = {};
    if (year) params.year = year;
    if (month) params.month = month;

    const res = await API_URL.get("/monthly", {
      ...getAuthHeader(),
      params,
    });
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.error || err.message };
  }
}

/**
 * 🏙️ Get city-wise rides
 */
export async function getCityWiseRides(startDate = null, endDate = null, type = "pickup") {
  try {
    const params = { type };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const res = await API_URL.get("/city-wise", {
      ...getAuthHeader(),
      params,
    });
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.error || err.message };
  }
}

/**
 * 🛺 Get driver-wise rides
 */
export async function getDriverWiseRides(startDate = null, endDate = null, page = 1, limit = 50) {
  try {
    const params = {
      n_page: page,
      n_limit: limit,
    };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const res = await API_URL.get("/driver-wise", {
      ...getAuthHeader(),
      params,
    });
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.error || err.message };
  }
}

/**
 * 📈 Get weekly chart data
 */
export async function getWeeklyChartData() {
  try {
    const res = await API_URL.get("/weekly-chart", getAuthHeader());
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.error || err.message };
  }
}

/**
 * ⏰ Get peak hours analysis
 */
export async function getPeakHoursAnalysis(days = 30) {
  try {
    const res = await API_URL.get("/peak-hours", {
      ...getAuthHeader(),
      params: { days },
    });
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.error || err.message };
  }
}

/**
 * 🗺️ Get rides for map view
 */
export async function getRidesForMap(date = null, status = "all", driverId = null, limit = 100) {
  try {
    const params = { n_limit: limit };
    if (date) params.date = date;
    if (status && status !== "all") params.status = status;
    if (driverId) params.driverId = driverId;

    const res = await API_URL.get("/map-rides", {
      ...getAuthHeader(),
      params,
    });
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.error || err.message };
  }
}
